import React, { FC, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import { UseFormReturn, useFieldArray, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { ReactComponent as RemoveAddonSVG } from "../../../../../../../../images/svg/subscriptions/remove-addon.svg";
import { organizationApi } from "../../../../../../../../services/Api/Organization/organizationApi";
import {
	AddonApi,
	SubscriptionApi,
	UpdateAddonApi,
	UpdateSubscriptionApi,
	UpdateSubscriptionsApi,
} from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import {
	PriceConfiguratorAddonApi,
	PriceConfiguratorAddonTermsApi,
	PriceConfiguratorContentApi,
} from "../../../../../../../../services/Api/types";
import { RenderApplicationLogo } from "../../../../../../components/RenderApplicationLogo/RenderApplicationLogo";
import ActiveSubscriptionMonthlyAddonsForm from "./ActiveSubscriptionMonthlyAddonsForm";
import AddonTermsModal from "./AddonTermsModal";
import SubscriptionFormStatus from "./SubscriptionFormStatus";
import SubscriptionUsageWarning from "./SubscriptionUsageWarning";
import YearlyAddons from "./YearlyAddons";
import { defaultAddon, isSubscriptionActive } from "./services";

interface Props {
	estimate?: SubscriptionApi;
	original?: SubscriptionApi;
	updateSubscription: UpdateSubscriptionApi;
	config: PriceConfiguratorContentApi;
	form: UseFormReturn<UpdateSubscriptionsApi>;
	index: number;
	handleRemove: () => void;
	handleResume: (planId?: string) => void;
	handleCancel: () => void;
	handleChangePlan: (familyId: string, planId: string) => void;
	setTermsNamesToAccept: React.Dispatch<React.SetStateAction<string[]>>;
	termsNamesToAccept: string[];
	estimating: boolean;
}

const ActiveSubscriptionForm: FC<Props> = (props: Props) => {
	const { t } = useTranslation();
	const {
		formState: { errors },
		control,
		setValue,
		watch,
		register,
	} = props.form;
	const { fields, remove, append } = useFieldArray({
		control,
		name: `subscriptions.${props.index}.addons`,
	});
	const isMobile = useWindowSize().isMobile;
	const watchedAddons: UpdateAddonApi[] | undefined = useWatch({
		name: `subscriptions.${props.index}.addons`,
		control,
	});
	const watchedPlan = watch(`subscriptions.${props.index}.plan_id`);
	const watchedPeriod = watch(`subscriptions.${props.index}.billing_period`);
	const plan = props.config?.plans.find((f) => f.plan_id === watchedPlan);
	const planAddons = props.config?.plans.find((f) => f.plan_id === watchedPlan)?.addons;
	const [addonTermsToAccept, setAddonTermsToAccept] = useState<PriceConfiguratorAddonTermsApi | undefined>();
	const [addonToTermsAccept, setAddonToTermsAccept] = useState<PriceConfiguratorAddonApi | undefined>();

	const getPeriodOptions = () => {
		return plan?.billing_periods?.map((period) => {
			return {
				label: t(`enums.billing_period.${period}`),
				value: period,
			};
		});
	};

	const isArchive = () => {
		if (plan && plan.license_level === "ARCHIVE") return true;
		return false;
	};
	const getArchivePlan = () => {
		return props.config?.plans.find((plan) => plan.license_level === "ARCHIVE");
	};

	const getPlanOptions = (currentValue: string | undefined) => {
		const allPlansWithEditableMode = props.config?.plans.filter((plan) => plan.edit_mode === "EDITABLE");
		if (currentValue) {
			const currentValuePlan = props.config.plans.find((plan) => plan.plan_id === currentValue);
			if (currentValuePlan && !currentValuePlan.edit_mode) {
				allPlansWithEditableMode.push(currentValuePlan);
			}
		}
		return allPlansWithEditableMode.map((plan) => {
			return {
				label: plan.name,
				value: plan.plan_id,
			};
		});
	};

	const isSubscriptionEditable = plan?.edit_mode === "EDITABLE";

	const onPauseSubscription = () => {
		const archivePlan = getArchivePlan();
		if (archivePlan) {
			setValue(`subscriptions.${props.index}.plan_id`, archivePlan.plan_id);
		} else {
			setValue(`subscriptions.${props.index}.status`, "PAUSED");
		}
	};

	const handleResumeSubscription = () => {
		const archivePlan = getArchivePlan();
		if (archivePlan) {
			props.handleResume(archivePlan.plan_id);
		} else {
			props.handleResume();
		}
	};
	const handleRemove = (addon: AddonApi, index: number) => {
		remove(index);
		handleRemoveTermsToAcceptForAddon(index);
	};
	const hasArchiveLicense = isArchive();

	const handleChangePlan = (value: string) => {
		if (!props.config) return;
		props.handleChangePlan(props.config?.family_id, value);
	};

	const handleAdd = async (configAddon: PriceConfiguratorAddonApi, skipRules?: boolean, termsAccepted?: boolean) => {
		if (!skipRules) {
			if (configAddon.terms) {
				const verifyTermsRes = await organizationApi.verifyTerms([configAddon.terms.name]);
				const addonTerms = verifyTermsRes.required_terms.find(
					(term) => term.terms_name === configAddon.terms?.name
				);

				if (addonTerms && !addonTerms.signed) {
					setAddonTermsToAccept(configAddon.terms);
					setAddonToTermsAccept(configAddon);
					return;
				}
			}
		}

		const newAddon = defaultAddon(configAddon);
		if (termsAccepted !== undefined && configAddon.terms && configAddon.terms.name) {
			if (termsAccepted) {
				props.setTermsNamesToAccept((prevState) => [...prevState, configAddon.terms!.name]);
			}

			newAddon.terms = {
				name: configAddon.terms.name,
				accepted: termsAccepted,
			};
		}
		if (newAddon.terms?.accepted === false) {
			return;
		}

		const exist = watchedAddons?.find((f) => f.addon_id === newAddon.addon_id);
		if (!exist) {
			remove();
			append([...(watchedAddons as UpdateAddonApi[]), newAddon]);
		} else {
			setValue(`subscriptions.${props.index}.addons.${watchedAddons?.length}`, newAddon);
		}
	};

	const onChangeBillingPeriod = (value: string) => {
		//CLEAR monthly subscription from update dto if changed to monthly
		if (value !== "YEARLY") {
			setValue(`subscriptions.${props.index}.monthly_sub_item`, undefined);
		}
	};

	const handleHideTermsModal = () => {
		setAddonToTermsAccept(undefined);
		setAddonTermsToAccept(undefined);
	};

	const handleRemoveTermsToAcceptForAddon = (index: number) => {
		const newTermsNames = [...props.termsNamesToAccept];

		const addon = watchedAddons[index];

		if (addon && addon.terms?.name && newTermsNames.includes(addon.terms.name)) {
			const termsNameIndex = newTermsNames.indexOf(addon.terms.name);
			newTermsNames.splice(termsNameIndex, 1);
		}
		props.setTermsNamesToAccept(newTermsNames);
	};

	return (
		<>
			<div className={`card subscription-card ${!isSubscriptionEditable && "readonly-subscription"}`}>
				<div className={"header"}>
					<RenderApplicationLogo slug={props.config.family_id} /> <h5>{plan?.name} </h5>
					<div className={"d-flex align-items-center"}>
						{props.estimate ? <SubscriptionFormStatus sub={props.estimate} /> : <></>}
					</div>
					<div className={"header-actions flex-wrap gap-1"}>
						{props.original ? (
							<>
								{props.estimate ? (
									<>
										{isSubscriptionActive(props.estimate.status) &&
											props.estimate.actual_subscription_exist && (
												<Button
													variant="add"
													style={{ color: "#DC3545", background: "transparent" }}
													onClick={() => props.handleCancel()}
												>
													{t("modules.subscription.action.cancel_subscription.title")}
												</Button>
											)}
										{!hasArchiveLicense &&
											props.estimate.status === "ACTIVE" &&
											props.estimate.actual_subscription_exist && (
												<Button variant={"add"} onClick={() => onPauseSubscription()}>
													{t("modules.subscription.action.stop_subscription.title")}
												</Button>
											)}
										{hasArchiveLicense && (
											<Button onClick={() => handleResumeSubscription()}>
												{t("modules.subscription.action.resume_subscription.title")}
											</Button>
										)}
									</>
								) : (
									<> ~ </>
								)}
							</>
						) : (
							<RemoveAddonSVG className="me-0" onClick={() => props.handleRemove()} />
						)}
					</div>
				</div>
				<div className={"body"}>
					<div className={"row"}>
						<FormInput
							errors={errors}
							type={"hidden"}
							register={register}
							name={`subscriptions.${props.index}.status`}
						/>
						{props.estimate && <SubscriptionUsageWarning subscription={props.estimate} />}
						<FormSelectGroup
							label={`${t("modules.subscription.field.license_type.title")}:`}
							errors={errors}
							options={getPlanOptions(props.estimate?.plan_id)}
							getOptionValue={(opt) => opt.value}
							getOptionLabel={(opt) => opt.label}
							name={`subscriptions.${props.index}.plan_id`}
							control={control}
							onChange={handleChangePlan}
							isClearable={false}
							data-testid={`subscriptions.${props.index}.plan_id`}
						/>
						<FormSelectGroup
							label={`${t("modules.subscription.field.period.title")}:`}
							errors={errors}
							onChange={onChangeBillingPeriod}
							getOptionLabel={(opt) => opt.label}
							getOptionValue={(opt) => opt.value}
							options={getPeriodOptions()}
							isClearable={false}
							name={`subscriptions.${props.index}.billing_period`}
							control={control}
							data-testid={`subscriptions.${props.index}.billing_period`}
							isSearchable={!isMobile}
						/>
						<div className={"subscription-price"}>
							<label className={"form-label"}>{t("modules.subscription.field.price.title")}:</label>
							<Alert variant={"warning"}>
								<strong>
									{props.estimate ? (
										<>
											{FormatMoney(props.estimate.total_money)}/
											{props.estimate.billing_period === "YEARLY" ? "rok" : "mies."}
											{props.estimate.monthly_sub_item &&
												props.estimate.monthly_sub_item.total_money && (
													<>
														{" "}
														+ {FormatMoney(props.estimate.monthly_sub_item.total_money)}/
														{t("modules.subscription.field.monthly_shortcut.title")}
													</>
												)}
										</>
									) : (
										<>~</>
									)}
								</strong>
							</Alert>
						</div>
					</div>
					{(fields.length > 0 || (planAddons && planAddons.length > 0)) && (
						<YearlyAddons
							fields={watchedAddons}
							index={props.index}
							form={props.form}
							configAddons={planAddons}
							estimate={props.estimate}
							handleRemove={handleRemove}
							handleAdd={handleAdd}
							estimating={props.estimating}
						/>
					)}
					{watchedPeriod === "YEARLY" && (
						<ActiveSubscriptionMonthlyAddonsForm
							config={props.config}
							form={props.form}
							index={props.index}
							estimate={props.estimate}
							handleRemoveAddonTermsNames={handleRemoveTermsToAcceptForAddon}
							estimating={props.estimating}
						/>
					)}
				</div>
			</div>
			{addonToTermsAccept && addonTermsToAccept && (
				<AddonTermsModal
					show
					onHide={handleHideTermsModal}
					terms={addonTermsToAccept}
					handleAddAddon={handleAdd}
					addon={addonToTermsAccept}
				/>
			)}
		</>
	);
};
export default ActiveSubscriptionForm;
